/**
 * 인생상회 장부 (시안 14 L1 · L5, 결정 60 · 63, 08 결정 28). 생일은 대화 속 장부로 묻고, 입력은 하단 시트(LedgerBirthSheet)로 받습니다.
 *
 *   왼쪽 40, --surface, hairline, 반경 16, 패딩 16, 간격 12. 머리: 수첩 아이콘 18 + 「인생상회 장부」(14/700 --brand)
 *   → 줄 셋(최소 40, 아래 점선, 라벨 폭 76 · 13): 이름 · 늘 마시는 차 · 생일(비면 「비어 있어요」 15)
 *   → offer 단계에서만 카드 안 [적어 주세요](주요, 44, 반경 14) [다음에요](보조). 누르면 버튼 칸이 200ms 로 접힘
 *
 * - 카드는 펼치지 않습니다(결정 60). 대화는 아래로만 쌓입니다
 * - 생일이 채워지면 그 줄 값이 300ms 로 나타나고 1.5초 동안 바탕 --brand 6% + 「방금 적음」(aria-live 「생일을 장부에 적었어요」)
 */
import { useEffect, useRef, useState } from 'react';
import { NotebookPen } from 'lucide-react';

const JUST_FILLED_MS = 1500;

function Row({ label, value, fresh = false }) {
    return (
        <div
            className={`-mx-2 flex min-h-10 items-center gap-2 rounded-[8px] border-b border-dashed border-[rgba(38,35,31,0.16)] px-2 transition-colors duration-300 last:border-b-0 ${
                fresh ? 'bg-brand/6' : ''
            }`}
        >
            <span className="w-[76px] shrink-0 text-[13px] text-text-secondary">{label}</span>
            {value ? (
                <span
                    key={value}
                    className="min-w-0 flex-1 font-serif text-[18px] font-bold motion-safe:animate-dim-in"
                >
                    {value}
                </span>
            ) : (
                <span className="flex-1 text-[15px] text-label-muted">비어 있어요</span>
            )}
            {fresh ? <span className="shrink-0 text-[11px] font-semibold text-brand">방금 적음</span> : null}
        </div>
    );
}

/** 생일 칸이 비었다가 채워진 순간부터 1.5초 */
function useJustFilled(value) {
    const previous = useRef(value);
    const [fresh, setFresh] = useState(false);
    useEffect(() => {
        const wasEmpty = !previous.current;
        previous.current = value;
        if (!wasEmpty || !value) {
            return undefined;
        }
        setFresh(true);
        const timer = setTimeout(() => setFresh(false), JUST_FILLED_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [value]);
    return fresh;
}

/**
 * @param {{ ledger: { displayName: string, tea: string|null, birth: string|null, status: string },
 *   step: string|null, busy: boolean, onAccept: () => void, onDecline: () => void }} props
 *   step 은 칩을 보여도 될 때만(이모가 말하는 중이면 null)
 */
export function LedgerCard({ ledger, step, busy, onAccept, onDecline }) {
    const offering = step === 'offer' && ledger.status === 'shown';
    const fresh = useJustFilled(ledger.birth);

    return (
        <section
            aria-label="인생상회 장부"
            className="relative ml-10 flex flex-col gap-3 rounded-card border border-hairline bg-surface p-4"
        >
            <span className="flex items-center gap-1.5">
                <NotebookPen size={18} strokeWidth={1.6} aria-hidden="true" className="text-brand" />
                <span className="text-[14px] font-bold text-brand">인생상회 장부</span>
            </span>
            <div className="flex flex-col">
                <Row label="이름" value={ledger.displayName} />
                <Row label="늘 마시는 차" value={ledger.tea} />
                <Row label="생일" value={ledger.birth} fresh={fresh} />
            </div>
            <span aria-live="polite" className="sr-only">
                {fresh ? '생일을 장부에 적었어요' : ''}
            </span>
            {ledger.status === 'shown' ? (
                // 버튼 칸은 접힐 때만 높이가 바뀝니다(200ms)
                <div
                    className={`grid transition-[grid-template-rows] duration-200 ease-out-soft ${
                        offering ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                    inert={offering ? undefined : true}
                    aria-hidden={offering ? undefined : true}
                >
                    <div className="flex gap-2 overflow-hidden">
                        <button
                            type="button"
                            disabled={busy || !offering}
                            onClick={onAccept}
                            className="h-11 flex-1 rounded-[14px] bg-brand text-[15px] font-semibold text-on-brand disabled:opacity-40"
                        >
                            적어 주세요
                        </button>
                        <button
                            type="button"
                            disabled={busy || !offering}
                            onClick={onDecline}
                            className="h-11 flex-1 rounded-[14px] border border-brand text-[15px] font-semibold text-brand disabled:opacity-40"
                        >
                            다음에요
                        </button>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
